import { findKey } from 'lodash'
import { FieldType } from '@wix/wix-data-schema-types'
import { DATA_BINDING } from '@wix/app-definition-ids'
import {
  updateSchema,
  addField,
  deleteFieldPermanently,
  updateField,
} from '@wix/wix-code-collections-api'

import type { FieldSpec, Schema } from '@wix/wix-data-schema-types'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type { FieldConfig } from '@wix/wix-code-collections-api/dist/types/types'

import {
  isPageLinkField,
  generateDefinition as generatePageLinkFieldDefinition,
  generateKey as generatePageLinkFieldKey,
  updatePattern,
  updateDisplayName,
} from './pageLinkField'
import { hasValidLinkedRouterPage } from '@wix/wix-data-client-common-standalone'

export interface PageLinkFieldDefinition {
  key: string
  definition: FieldSpec
}

export const getFields = async (
  collectionsApi: CollectionsApi,
  collectionId: string,
) => (await collectionsApi.get(collectionId)).fields

const addFieldsToSchema = async ({
  collectionsApi,
  collectionId,
  definitions,
}: {
  collectionsApi: CollectionsApi
  collectionId: string
  definitions: {
    key: string
    definition: FieldConfig
  }[]
}) =>
  collectionsApi.execute(
    updateSchema(
      collectionId,
      definitions.map(({ key, definition }) => addField(key, definition)),
    ),
  )

const removeFieldsFromSchema = async (
  collectionsApi: CollectionsApi,
  collectionId: string,
  fieldKeys: string[],
): Promise<void> => {
  if (!fieldKeys.length) {
    return
  }
  const patches = fieldKeys.map(fieldKey => deleteFieldPermanently(fieldKey))
  return collectionsApi.execute(updateSchema(collectionId, ...patches))
}

const updateFieldInSchema = (
  collectionsApi: CollectionsApi,
  collectionId: string,
  fieldKey: string,
  fieldConfig: FieldConfig,
) =>
  collectionsApi.execute(
    updateSchema(collectionId, updateField(fieldKey, fieldConfig)),
  )

const getAllSchemas = (collectionsApi: CollectionsApi) =>
  collectionsApi.getAll()

export const createPageLinkFields = async function ({
  collectionsApi,
  collectionId,
  fieldsData,
}: {
  collectionsApi: CollectionsApi
  collectionId: string
  fieldsData: {
    key: string
    displayName: string
    prefix: string
    pattern: string
    pageId: string
  }[]
}): Promise<void> {
  const definitions = fieldsData.map(
    ({ key, displayName, prefix, pattern, pageId }) => ({
      key,
      definition: {
        isDeleted: false,
        systemField: true,
        plugin: DATA_BINDING,
        ...generatePageLinkFieldDefinition({
          pageTitle: displayName,
          prefix,
          pattern,
          pageId,
        }),
      },
    }),
  )

  await addFieldsToSchema({ collectionsApi, collectionId, definitions })
}

export const getPageLinkField = function (
  collectionFields: Schema['fields'],
  prefix: string,
  pattern: string,
  pageId?: string,
): PageLinkFieldDefinition | null {
  let calculatedFieldKey = findKey(collectionFields, field =>
    isPageLinkField(field, prefix, pattern),
  )
  if (!calculatedFieldKey && pageId) {
    calculatedFieldKey = findKey(
      collectionFields,
      field =>
        field.type === FieldType.pageLink && field.linkedRouterPage === pageId,
    )
  }
  if (!calculatedFieldKey) {
    return null
  }
  return {
    key: calculatedFieldKey,
    definition: collectionFields[calculatedFieldKey],
  }
}

export const deletePageLinkFields = async function (
  collectionsApi: CollectionsApi,
  collectionId: string,
  prefix: string,
  pattern: string,
): Promise<void> {
  const schemas = await getAllSchemas(collectionsApi)
  const schema = schemas.find(({ id }) => id === collectionId)
  if (!schema) {
    return
  }

  const fieldKeysToRemove = Object.keys(schema.fields).filter(fieldKey => {
    const field = schema.fields[fieldKey]
    return isPageLinkField(field, prefix, pattern)
  })
  await removeFieldsFromSchema(collectionsApi, collectionId, fieldKeysToRemove)
}

export const addPageLinkField = async ({
  collectionsApi,
  collectionId,
  routerPrefix,
  route,
  pageId,
  displayName,
}: {
  collectionsApi: CollectionsApi
  collectionId: string
  routerPrefix: string
  route: string
  pageId: string
  displayName: string
}): Promise<{ pageLinkFieldKey: string }> => {
  const collectionFields = await getFields(collectionsApi, collectionId)
  const existingFieldKeys = Object.keys(collectionFields)

  const pageLinkFieldKey = generatePageLinkFieldKey({
    prefix: routerPrefix,
    pattern: route,
    existingFieldKeys,
  })

  await createPageLinkFields({
    collectionsApi,
    collectionId,
    fieldsData: [
      {
        key: pageLinkFieldKey,
        displayName,
        prefix: routerPrefix,
        pattern: route,
        pageId,
      },
    ],
  })

  return { pageLinkFieldKey }
}

const updatePageLinkField = async function ({
  collectionsApi,
  collectionId,
  originalPageLinkField,
  newDisplayName,
  newPrefix,
  newPattern,
  pageId,
}: {
  collectionsApi: CollectionsApi
  collectionId: string
  originalPageLinkField: PageLinkFieldDefinition
  newDisplayName: string
  newPrefix: string
  newPattern: string
  pageId: string
}) {
  let updatedPageFieldDef = updatePattern(
    originalPageLinkField.definition,
    newPrefix,
    newPattern,
  )
  updatedPageFieldDef = updateDisplayName(updatedPageFieldDef, newDisplayName)

  if (!hasValidLinkedRouterPage(updatedPageFieldDef.linkedRouterPage)) {
    updatedPageFieldDef.linkedRouterPage = pageId
  }

  await updateFieldInSchema(
    collectionsApi,
    collectionId,
    originalPageLinkField.key,
    updatedPageFieldDef,
  )
}

export const updateOrCreatePageLinkField = async function ({
  collectionsApi,
  collectionId,
  oldPrefix,
  oldPattern,
  newDisplayName,
  newPrefix,
  newPattern,
  pageId,
}: {
  collectionsApi: CollectionsApi
  collectionId: string
  oldPrefix: string
  oldPattern: string
  newDisplayName: string
  newPrefix: string
  newPattern: string
  pageId: string
}) {
  const originalPageLinkField = getPageLinkField(
    await getFields(collectionsApi, collectionId),
    oldPrefix,
    oldPattern,
    pageId,
  )
  return originalPageLinkField
    ? updatePageLinkField({
        collectionsApi,
        collectionId,
        originalPageLinkField,
        newDisplayName,
        newPrefix,
        newPattern,
        pageId,
      })
    : addPageLinkField({
        collectionsApi,
        collectionId,
        routerPrefix: newPrefix,
        route: newPattern,
        pageId,
        displayName: newDisplayName,
      })
}
